import React, { useEffect, useState } from 'react';
import { useAuthStore } from '@store/authStore';
import { useOrgStore } from '@store/orgStore';
import { useListOrgHierarchies } from '@api/hooks/useOrgHierarchies';
import { useListOrgUnits, useGetOrgUnit, useCreateOrgUnit, useUpdateOrgUnit, useDeleteOrgUnit } from '@api/hooks/useOrgUnits';
import { useAddOrgMember, useRemoveOrgMember } from '@api/hooks/useOrgMembers';
import { useListUsers } from '@api/hooks/useUsers';
import { OrgTreeExplorer } from './OrgTreeExplorer';
import { OrgUnitDetailPanel } from './OrgUnitDetailPanel';
import { OrgUnitForm } from './OrgUnitForm';
import { AddMemberModal } from './AddMemberModal';
import { ConfirmActionModal } from './ConfirmActionModal';
import { Plus, List, GitBranch } from 'lucide-react';
import type { OrgUnit, CreateOrgUnitRequest } from '@schemas/api';
import OrgStructureTree from './OrgStructureTree';

const UNIT_TYPES = ['Department', 'Team', 'Division', 'Group', 'Squad'];

type ViewMode = 'tree' | 'detail';

export const OrgStructurePage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('tree');

  const currentUser = useAuthStore((state) => state.currentUser);
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const canEdit = hasPermission('org_unit.write');

  // Store state
  const selectedUnitId = useOrgStore((state) => state.selectedUnitId);
  const selectedHierarchyId = useOrgStore((state) => state.selectedHierarchyId);
  const editingUnitId = useOrgStore((state) => state.editingUnitId);
  const showAddMemberModal = useOrgStore((state) => state.showAddMemberModal);
  const showConfirmModal = useOrgStore((state) => state.showConfirmModal);
  const confirmAction = useOrgStore((state) => state.confirmAction);

  const selectUnit = useOrgStore((state) => state.selectUnit);
  const selectHierarchy = useOrgStore((state) => state.selectHierarchy);
  const setEditingUnit = useOrgStore((state) => state.setEditingUnit);
  const setShowAddMemberModal = useOrgStore((state) => state.setShowAddMemberModal);
  const setConfirmAction = useOrgStore((state) => state.setConfirmAction);
  const setShowConfirmModal = useOrgStore((state) => state.setShowConfirmModal);

  // Queries
  const hierarchiesQuery = useListOrgHierarchies();
  const unitsQuery = useListOrgUnits(selectedHierarchyId);
  const selectedUnitDetailsQuery = useGetOrgUnit(selectedUnitId);
  const usersQuery = useListUsers(0, 100); // Fetch up to 100 users for member selection (API max is 100)

  // Mutations
  const createUnitMutation = useCreateOrgUnit();
  const updateUnitMutation = useUpdateOrgUnit();
  const deleteUnitMutation = useDeleteOrgUnit();
  const addMemberMutation = useAddOrgMember();
  const removeMemberMutation = useRemoveOrgMember();

  // Select first hierarchy on load
  useEffect(() => {
    if (!selectedHierarchyId && hierarchiesQuery.data?.length) {
      selectHierarchy(hierarchiesQuery.data[0].id);
    }
  }, [hierarchiesQuery.data, selectedHierarchyId, selectHierarchy]);

  // Find selected unit and its details
  const findUnit = (units: OrgUnit[], unitId: string): OrgUnit | undefined => {
    for (const unit of units) {
      if (unit.id === unitId) return unit;
      if (unit.children) {
        const found = findUnit(unit.children, unitId);
        if (found) return found;
      }
    }
    return undefined;
  };

  // Use detailed unit query for selected unit to get members, or fallback to list data
  const selectedUnit = selectedUnitDetailsQuery.data || (selectedUnitId
    ? findUnit(unitsQuery.data || [], selectedUnitId)
    : null);


  const handleCreateUnit = (data: any) => {
    const payload: CreateOrgUnitRequest = {
      hierarchy_id: selectedHierarchyId!,
      parent_id: data.parent_id || undefined,
      name: data.name,
      unit_type: data.unit_type,
      description: data.description || undefined,
      manager_id: data.manager_id || undefined,
    };
    createUnitMutation.mutate(payload, {
      onSuccess: () => {
        setEditingUnit(null);
      },
    });
  };

  const handleUpdateUnit = (data: any) => {
    if (!editingUnitId) return;
    updateUnitMutation.mutate(
      {
        unitId: editingUnitId,
        hierarchyId: selectedHierarchyId!,
        data: {
          name: data.name,
          description: data.description || undefined,
          manager_id: data.manager_id || undefined,
          parent_id: data.parent_id || undefined,
        },
      },
      {
        onSuccess: () => {
          setEditingUnit(null);
        },
      }
    );
  };

  const handleDeleteUnit = () => {
    if (!selectedUnitId) return;
    setShowConfirmModal(false);
    setConfirmAction(null);
    deleteUnitMutation.mutate({
      unitId: selectedUnitId,
      hierarchyId: selectedHierarchyId!,
    });
  };

  const handleAddMember = (userId: string, membershipType: string) => {
    if (!selectedUnitId) return;
    addMemberMutation.mutate(
      {
        unitId: selectedUnitId,
        request: {
          user_id: userId,
          membership_type: membershipType,
        },
      },
      {
        onSuccess: () => {
          setShowAddMemberModal(false);
        },
      }
    );
  };

  const handleRemoveMember = (unitId: string, memberId: string) => {
    removeMemberMutation.mutate({ unitId, memberId });
  };

  const handleUnitMove = (unitId: string, newParentId: string | null) => {
    updateUnitMutation.mutate({
      unitId,
      hierarchyId: selectedHierarchyId!,
      data: { name: findUnit(unitsQuery.data || [], unitId)?.name || '' },
    });
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Organization Structure</h1>
        <div className="flex gap-2 items-center">
          {/* View Mode Toggle */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('tree')}
              className={`flex items-center gap-1 px-3 py-1 rounded transition-colors ${
                viewMode === 'tree'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Unified tree view"
            >
              <GitBranch size={16} />
              <span className="text-sm font-medium">Tree</span>
            </button>
            <button
              onClick={() => setViewMode('detail')}
              className={`flex items-center gap-1 px-3 py-1 rounded transition-colors ${
                viewMode === 'detail'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Detail panel view"
            >
              <List size={16} />
              <span className="text-sm font-medium">Detail</span>
            </button>
          </div>

          {canEdit && (
            <div className="flex gap-2">
              {selectedHierarchyId && (
                <button
                  onClick={() => setEditingUnit('')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus size={18} />
                  Create Unit
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 min-h-0">
        {viewMode === 'tree' ? (
          /* Unified Tree View */
          <OrgStructureTree hierarchyId={selectedHierarchyId} />
          // <UnifiedOrgTreeView
          //   units={unitsQuery.data || []}
          //   isLoading={unitsQuery.isLoading}
          //   canEdit={canEdit}
          //   onEdit={(unitId) => setEditingUnit(unitId)}
          //   onDelete={(unitId) => {
          //     setConfirmAction({ type: 'delete_unit', unitId });
          //     setShowConfirmModal(true);
          //   }}
          //   onAddMember={(unitId) => setShowAddMemberModal(true)}
          //   onRemoveMember={(unitId, memberId) => {
          //     setConfirmAction({
          //       type: 'remove_member',
          //       unitId,
          //       itemId: memberId,
          //     });
          //     setShowConfirmModal(true);
          //   }}
          // />
        ) : (
          /* Detail Panel View */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full">
            {/* Left panel - Tree Explorer */}
            <div className="lg:col-span-1">
              <OrgTreeExplorer
                units={unitsQuery.data || []}
                isLoading={unitsQuery.isLoading}
                canEdit={canEdit}
                onUnitSelect={selectUnit}
                onUnitMove={handleUnitMove}
              />
            </div>

            {/* Right panel - Unit Details */}
            <div className="lg:col-span-2">
              <OrgUnitDetailPanel
                unit={selectedUnit || null}
                isLoading={selectedUnitDetailsQuery.isLoading}
                canEdit={canEdit}
                onEdit={(unitId) => setEditingUnit(unitId)}
                onDelete={(unitId) => {
                  setConfirmAction({ type: 'delete_unit', unitId });
                  setShowConfirmModal(true);
                }}
                onAddMember={(unitId) => setShowAddMemberModal(true)}
                onRemoveMember={(unitId, memberId) => {
                  setConfirmAction({
                    type: 'remove_member',
                    unitId,
                    itemId: memberId,
                  });
                  setShowConfirmModal(true);
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <OrgUnitForm
        isOpen={editingUnitId !== null}
        isLoading={createUnitMutation.isPending || updateUnitMutation.isPending}
        unit={editingUnitId ? findUnit(unitsQuery.data || [], editingUnitId) : null}
        parentId={selectedUnitId}
        hierarchyId={selectedHierarchyId || ''}
        unitTypes={UNIT_TYPES}
        onSubmit={editingUnitId ? handleUpdateUnit : handleCreateUnit}
        onClose={() => setEditingUnit(null)}
      />

      <AddMemberModal
        isOpen={showAddMemberModal}
        isLoading={addMemberMutation.isPending}
        unitName={selectedUnit?.name || ''}
        availableUsers={usersQuery.data?.items || []}
        onSubmit={handleAddMember}
        onClose={() => setShowAddMemberModal(false)}
      />

      <ConfirmActionModal
        isOpen={showConfirmModal}
        isLoading={
          deleteUnitMutation.isPending || removeMemberMutation.isPending
        }
        title={confirmAction?.type === 'delete_unit' ? 'Delete Unit' : 'Remove Member'}
        message={
          confirmAction?.type === 'delete_unit'
            ? 'Are you sure you want to delete this unit and all its members?'
            : 'Are you sure you want to remove this member?'
        }
        confirmText={confirmAction?.type === 'delete_unit' ? 'Delete' : 'Remove'}
        isDangerous
        onConfirm={() => {
          if (confirmAction?.type === 'delete_unit') {
            handleDeleteUnit();
          } else if (confirmAction?.type === 'remove_member') {
            handleRemoveMember(confirmAction.unitId, confirmAction.itemId!);
          }
        }}
        onCancel={() => {
          setShowConfirmModal(false);
          setConfirmAction(null);
        }}
      />
    </div>
  );
};
